import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App";
import "./index.css";

// CSRF defence-in-depth (EXP-10). The API rejects any cookie-authenticated
// state-changing request that lacks an Authorization bearer OR the custom
// X-Requested-With header. The generated api-client already sends a bearer;
// this global wrapper stamps X-Requested-With on every SAME-ORIGIN fetch so
// hand-written fetch() calls (which rely on the Clerk session cookie) keep
// working. A cross-site form/script cannot set this header without tripping
// a CORS preflight the server's allow-list then blocks. Cross-origin
// requests are left untouched to avoid forcing needless preflights.
(() => {
  if (typeof window === "undefined" || !window.fetch) return;
  const originalFetch = window.fetch.bind(window);
  const isSameOrigin = (input: RequestInfo | URL): boolean => {
    try {
      const url =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;
      return new URL(url, window.location.href).origin === window.location.origin;
    } catch {
      // Relative URLs that fail to parse against location are same-origin.
      return true;
    }
  };
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    if (!isSameOrigin(input)) return originalFetch(input, init);
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (!headers.has("x-requested-with")) headers.set("x-requested-with", "fetch");
    return originalFetch(input, { ...init, headers });
  };
})();

// Shared Duffells Clerk instance. The LIVE key is used for production
// builds, the TEST key for development — both point at the same shared
// tenant but different environments.
const PUBLISHABLE_KEY = (
  import.meta.env.PROD
    ? import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_LIVE
    : import.meta.env.VITE_CLERK_PUBLISHABLE_KEY_TEST
) as string | undefined;

if (!PUBLISHABLE_KEY) {
  throw new Error(
    "Missing Clerk publishable key. Set VITE_CLERK_PUBLISHABLE_KEY_TEST (dev) " +
      "or VITE_CLERK_PUBLISHABLE_KEY_LIVE (prod).",
  );
}

// Strip the trailing slash so Clerk's redirect URLs sit under the app's
// base path (the app is served from import.meta.env.BASE_URL).
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "") || "/";

createRoot(document.getElementById("root")!).render(
  <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl={basePath}>
    <App />
  </ClerkProvider>,
);
